import React, { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { generateBinQRUrl } from '../../config/qrConfig';
import './BinLabelPreview.css';

function BinLabelPreview({ bin, labelType }) {
  if (!bin) return null;

  const qrUrl = generateBinQRUrl(bin.bin_code);

  // Render based on label type
  switch (labelType) {
    case 'floor':
      return <FloorLabelPreview bin={bin} qrUrl={qrUrl} />;
    case 'shelf':
      return <ShelfLabelPreview bin={bin} qrUrl={qrUrl} />;
    case 'pallet':
    default:
      return <PalletLabelPreview bin={bin} qrUrl={qrUrl} />;
  }
}

// Floor Label - 10x8 Landscape - Large format for floor bins
function FloorLabelPreview({ bin, qrUrl }) {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && bin.bin_code) {
      try {
        JsBarcode(barcodeRef.current, bin.bin_code, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed:', error);
      }
    }
  }, [bin.bin_code]);

  return (
    <div className="bin-label-preview floor-preview">
      <div className="floor-label-content">
        <div className="floor-top-row">
          <div className="floor-left">
            <div className="floor-bin-code">{bin.bin_code}</div>
          </div>
          <div className="floor-right">
            <div className="floor-barcode">
              <svg ref={barcodeRef}></svg>
            </div>
            <div className="floor-qr-section">
              <QRCodeSVG value={qrUrl} size={100} level="M" includeMargin={false} />
            </div>
          </div>
        </div>
        {bin.set_sku && (
          <div className="floor-sku-row">
            <div className="floor-set-sku">{bin.set_sku}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Shelf Label - 1x3 - Compact horizontal strip
function ShelfLabelPreview({ bin, qrUrl }) {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && bin.bin_code) {
      try {
        JsBarcode(barcodeRef.current, bin.bin_code, {
          format: 'CODE128',
          width: 1,
          height: 25,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed:', error);
      }
    }
  }, [bin.bin_code]);

  return (
    <div className="bin-label-preview shelf-preview">
      <div className="shelf-label-content">
        <div className="shelf-qr">
          <QRCodeSVG value={qrUrl} size={40} level="M" includeMargin={false} />
        </div>
        <div className="shelf-barcode">
          <svg ref={barcodeRef}></svg>
        </div>
        <div className="shelf-text">
          <div className="shelf-bin-code">{bin.bin_code}</div>
        </div>
      </div>
    </div>
  );
}

// Pallet Label - 4x6 - Medium format for racking
function PalletLabelPreview({ bin, qrUrl }) {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (barcodeRef.current && bin.bin_code) {
      try {
        JsBarcode(barcodeRef.current, bin.bin_code, {
          format: 'CODE128',
          width: 2,
          height: 50,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed:', error);
      }
    }
  }, [bin.bin_code]);

  return (
    <div className="bin-label-preview pallet-preview">
      <div className="pallet-label-content">
        <div className="pallet-header">
          <div className="pallet-bin-code">{bin.bin_code}</div>
        </div>
        <div className="pallet-body">
          <div className="pallet-left">
            <div className="pallet-barcode">
              <svg ref={barcodeRef}></svg>
            </div>
          </div>
          <div className="pallet-right">
            <QRCodeSVG value={qrUrl} size={100} level="M" includeMargin={false} />
            <div className="pallet-qr-label">SCAN</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BinLabelPreview;
